// src/utils/rateLimiter.ts

/**
 * Simple rate limiter for client-side operations
 * Prevents brute force attacks and excessive API calls
 */
export class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  /**
   * @param maxAttempts - Maximum number of attempts allowed
   * @param windowMs - Time window in milliseconds
   */
  constructor(maxAttempts: number = 5, windowMs: number = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  /**
   * Check if an action is allowed based on rate limit
   * @param key - Identifier for the action (e.g., user email, IP)
   * @returns true if allowed, false if rate limited
   */
  public isAllowed(key: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];

    // Filter out attempts outside the time window
    const recentAttempts = attempts.filter(
      (timestamp) => now - timestamp < this.windowMs
    );

    // Check if user exceeded max attempts
    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }

    // Record this attempt
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);

    // Clean up old entries periodically
    this.cleanup();

    return true;
  }

  /**
   * Get remaining attempts for a key
   * @param key - Identifier for the action
   * @returns number of remaining attempts
   */
  public getRemainingAttempts(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];

    const recentAttempts = attempts.filter(
      (timestamp) => now - timestamp < this.windowMs
    );

    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }

  /**
   * Get time until next allowed attempt
   * @param key - Identifier for the action
   * @returns milliseconds until next attempt, or 0 if allowed now
   */
  public getTimeUntilReset(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];

    if (attempts.length === 0) return 0;

    const oldestAttempt = Math.min(...attempts);
    const timeSinceOldest = now - oldestAttempt;

    if (timeSinceOldest >= this.windowMs) return 0;

    return this.windowMs - timeSinceOldest;
  }

  /**
   * Reset attempts for a key
   * @param key - Identifier for the action
   */
  public reset(key: string): void {
    this.attempts.delete(key);
  }

  /**
   * Clean up old entries from memory
   */
  private cleanup(): void {
    const now = Date.now();

    // Only cleanup every 5 minutes to avoid performance impact
    if (
      !this.lastCleanup ||
      now - this.lastCleanup > 5 * 60 * 1000
    ) {
      for (const [key, attempts] of this.attempts.entries()) {
        const recentAttempts = attempts.filter(
          (timestamp) => now - timestamp < this.windowMs
        );

        if (recentAttempts.length === 0) {
          this.attempts.delete(key);
        } else {
          this.attempts.set(key, recentAttempts);
        }
      }

      this.lastCleanup = now;
    }
  }

  private lastCleanup: number = 0;
}

// Global rate limiters for different operations
export const loginRateLimiter = new RateLimiter(5, 60000); // 5 attempts per minute
export const apiRateLimiter = new RateLimiter(30, 60000); // 30 requests per minute
